<template>
  <VeeForm
    v-slot="{ errors, handleSubmit, meta }"
    as=""
    :validate-on-mount="validateOnMount"
  >
    <form
      autocomplete="off"
      class="p-4 rounded-md mt-2 w-full overflow-auto"
      :class="
        isOrderBuy
          ? 'bg-brand-primary bg-opacity-10 border border-brand-primary border-opacity-10'
          : 'bg-critical-secondary bg-opacity-90 border border-critical-secondary border-opacity-20 darkTheme:border-opacity-10 darkTheme:bg-opacity-50'
      "
      @submit="handleSubmit($event, onSubmit)"
    >
      <!-- Bonus Share -->
      <template v-if="!isOrderBuy && Boolean(bonusShare)">
        <div
          class="flex justify-start items-center gap-1 mb-2 flex-1 cursor-pointer"
          @click="
            openBonusShareDialog(getSymbolName(symbol), bonusShare?.quantity)
          "
        >
          <NegarErrorBoldIcon size="16" class="text-warning-secondary" />

          <NText
            variant="overline"
            class="text-neutral-secondary whitespace-nowrap text-ellipsis overflow-hidden"
          >
            {{ t('سهام جایزه تا زمان تبدیل شدن به سهم') }}
            <NText variant="overline" weight="bold">
              {{ t('غیر قابل معامله') }}
            </NText>
            {{ t('است.') }}
          </NText>

          <NegarArrowLeftIcon size="16" />
        </div>
      </template>

      <VeeField
        v-slot="{ field }"
        v-model="formModel.quantity"
        name="quantity"
        :rules="
          isUnderWriting
            ? {
                required: true,
                max: [symbol.maximumOrderQuantity, t('modules.order.order')],
                min: [
                  symbol.minimumOrderQuantity,
                  t('modules.order.order_count'),
                ],
                quantityTick: symbol.lotSize,
              }
            : {
                required: true,
                min: [
                  symbol.minimumOrderQuantity,
                  t('modules.order.order_count'),
                ],
                quantityTick: symbol.lotSize,
              }
        "
      >
        <NInput
          v-mask="{
            alias: 'positiveNumber',
          }"
          v-focus
          :model-value="formModel.quantity"
          input-class="text-center font-bold"
          v-bind="field"
          :error="errors.quantity"
          :required-sign="false"
          :show-error="true"
          type="tel"
          dir="ltr"
          class="mb-3"
          required
          size="small"
          :step="symbol.lotSize"
          :min="0"
          @focusin="$event.target.select()"
          @update:model-value="changeQuantity($event)"
        >
          <template #prefix>
            <NText
              variant="caption1"
              weight="medium"
              class="text-neutral-primary"
            >
              {{ t('misc.quantity') }}
            </NText>
          </template>

          <template #postfix>
            <div class="flex justify-center items-center">
              <div class="flex flex-col cursor-pointer">
                <NumberFormatter
                  #="{ number }"
                  :value="symbol.maximumOrderQuantity"
                >
                  <NText
                    dir="ltr"
                    tag="p"
                    variant="caption2"
                    @click="changeQuantity(symbol.maximumOrderQuantity)"
                  >
                    {{ number }}
                  </NText>
                </NumberFormatter>
                <NumberFormatter
                  #="{ number }"
                  :value="symbol.minimumOrderQuantity"
                >
                  <NText
                    dir="ltr"
                    tag="p"
                    variant="caption2"
                    @click="changeQuantity(symbol.minimumOrderQuantity)"
                  >
                    {{ number }}
                  </NText>
                </NumberFormatter>
              </div>
              <div v-if="!isEditOrder && !isUnderWriting">
                <NegarFlashLightIcon
                  v-if="!isFlashTrading"
                  class="ms-2 cursor-pointer"
                  :class="[
                    hasSuggestions
                      ? 'text-brand-primary'
                      : 'text-neutral-secondary',
                  ]"
                  @click="hasSuggestions = !hasSuggestions"
                />
                <NegarFlashBoldIcon
                  v-if="isFlashTrading"
                  class="text-brand-primary ms-2 cursor-pointer"
                />
              </div>
            </div>
          </template>
        </NInput>
      </VeeField>

      <template v-if="!isEditOrder && !isUnderWriting">
        <FlashTrading
          :has-suggestions="hasSuggestions"
          :form-model="formModel"
          :symbol="symbol"
          :is-flash-trading="isFlashTrading"
          :max-quantity="MAXIMUM_QUANTITY"
          @change-quantity="changeQuantity($event)"
        />
      </template>

      <div v-if="!isOrderBuy" class="flex justify-between items-center px-3">
        <NText variant="caption1">
          {{ `${t('modules.order.portfolio_quantity')}:` }}
        </NText>

        <div>
          <NumberFormatter #="{ number }" :value="portfolioQuantity">
            <NText
              variant="caption1"
              class="cursor-pointer"
              @click="changeQuantity(portfolioQuantity)"
            >
              {{ number }}
            </NText>
          </NumberFormatter>
        </div>
      </div>

      <Slider
        v-if="!isOrderBuy && portfolioQuantity"
        :model-value="formModel.quantity ?? 0"
        :max-value="portfolioQuantity"
        color="red"
        @change="changeQuantity"
      />

      <VeeField
        v-slot="{ field }"
        v-model="formModel.price"
        name="price"
        :rules="{
          required: true,
          between: [
            symbol.lowerPriceThreshold,
            symbol.upperPriceThreshold,
            t('modules.order.order_price'),
          ],
          priceTick: symbol.fixedPriceTick,
        }"
      >
        <NInput
          v-mask="{
            alias: 'positiveNumber',
          }"
          :model-value="formModel.price"
          input-class="text-center font-bold"
          v-bind="field"
          :error="errors.price"
          :required-sign="false"
          size="small"
          :show-error="true"
          type="tel"
          dir="ltr"
          required
          class="mb-3"
          :min="0"
          :step="symbol.fixedPriceTick"
          :disabled="isLinkToPrice"
          @focusin="$event.target.select()"
          @update:model-value="changePrice($event)"
        >
          <template #prefix>
            <NText
              variant="caption1"
              weight="medium"
              class="text-neutral-primary"
            >
              {{ t('common.price') }}
            </NText>
          </template>

          <template #postfix>
            <div class="flex justify-center items-center">
              <div class="flex flex-col cursor-pointer">
                <NumberFormatter
                  #="{ number }"
                  :value="symbol.upperPriceThreshold"
                >
                  <NText
                    v-gid="{
                      module: 'order',
                      screen: 'home',
                      section: `${type}OrderDialog`,
                      component: 'maxPriceButton',
                    }"
                    dir="ltr"
                    tag="p"
                    variant="caption2"
                    @click="changePrice(symbol.upperPriceThreshold)"
                  >
                    {{ number }}
                  </NText>
                </NumberFormatter>
                <NumberFormatter
                  #="{ number }"
                  :value="symbol.lowerPriceThreshold"
                >
                  <NText
                    v-gid="{
                      module: 'order',
                      screen: 'home',
                      section: `${type}OrderDialog`,
                      component: 'minPriceButton',
                    }"
                    dir="ltr"
                    tag="p"
                    variant="caption2"
                    @click="changePrice(symbol.lowerPriceThreshold)"
                  >
                    {{ number }}
                  </NText>
                </NumberFormatter>
              </div>

              <NegarLinkIcon
                class="ms-2 cursor-pointer"
                :class="{ 'text-brand-primary': isLinkToPrice }"
                @click="changeLink"
              />
            </div>
          </template>
        </NInput>
      </VeeField>

      <VeeField
        v-slot="{ field }"
        v-model="formModel.totalValue"
        name="totalValue"
        :rules="{
          required: true,
        }"
      >
        <NInput
          v-mask="{
            alias: 'positiveNumber',
          }"
          :model-value="formModel.totalValue"
          v-bind="field"
          input-class="text-center font-bold"
          :error="errors.totalValue"
          :required-sign="false"
          :show-error="true"
          type="tel"
          dir="ltr"
          class="mb-3"
          size="small"
          :min="0"
          required
          @focusin="$event.target.select()"
          @update:model-value="changeTotalValue($event)"
        >
          <template #prefix>
            <NText
              variant="caption1"
              weight="medium"
              class="text-neutral-primary"
            >
              {{ t('misc.value') }}
            </NText>
          </template>

          <template #postfix>
            <NText variant="caption1" class="text-neutral-secondary">
              {{ t('misc.rial') }}
            </NText>
          </template>
        </NInput>
      </VeeField>
      <template v-if="isOrderBuy">
        <div
          class="flex justify-between items-center px-3"
          :class="{
            'text-neutral-tertiary-disable': cash.buyingPower <= 0,
          }"
        >
          <NText variant="caption1">
            {{ t('common.buy_power') }}
          </NText>

          <NLoading v-if="isLoadingCash" class="text-brand-primary" />
          <div v-else>
            <NumberFormatter #="{ number }" :value="cash.buyingPower" round>
              <NText
                dir="ltr"
                variant="caption1"
                weight="bold"
                class="cursor-pointer"
                :class="{ 'cursor-default': cash.buyingPower <= 0 }"
                @click="changeTotalValue(cash.buyingPower)"
              >
                {{ number }}
              </NText>
            </NumberFormatter>
            <NText
              variant="caption2"
              :class="{
                'text-neutral-tertiary-disable': cash.buyingPower <= 0,
              }"
              class="text-neutral-secondary ms-0.5 align-middle"
            >
              {{ t('misc.rial') }}
            </NText>
          </div>
        </div>

        <Slider
          v-if="cash.buyingPower && cash.buyingPower > 0"
          :model-value="formModel.totalValue ?? 0"
          :max-value="maximumBuyingPower"
          @change="changeTotalValue"
        />
      </template>

      <div v-if="isShowValidityTypeOptions" class="mt-6 text-center">
        <template v-for="(value, key) in VALIDITY_TYPES" :key="key">
          <button
            class="px-3 py-2 rounded-md ml-1 mb-2 cursor-pointer"
            :class="[
              formModel.validityType === value
                ? validityTypeButtonClasses[type]
                : 'text-neutral-secondary-hover',
            ]"
            type="button"
            @click="formModel.validityType = value"
          >
            <NText
              variant="caption2"
              :weight="[formModel.validityType === value ? 'bold' : 'regular']"
            >
              {{ VALIDITY_TYPE_LABEL[value] }}
            </NText>
          </button>
        </template>
      </div>

      <Datepicker
        v-if="formModel.validityType === VALIDITY_TYPES.GoodTillDate"
        v-model="formModel.validityDate"
        is-local-date-time
        class="mt-3 mb-2 w-auto"
      />

      <div class="flex justify-center mb-28">
        <div
          class="flex items-center justify-center mb-2 p-2 active:bg-neutral-primary-press w-fit rounded-md cursor-pointer"
          @click="isShowValidityTypeOptions = !isShowValidityTypeOptions"
        >
          <NText>
            {{ validityTypeLabel }}
          </NText>
          <NegarArrowDownIcon
            :size="20"
            :class="[
              'transform transition',
              {
                'rotate-180': isShowValidityTypeOptions,
              },
            ]"
          />
        </div>
      </div>

      <div
        class="bg-neutral-primary border-t border-t-neutral-primary w-full p-3 rounded-b-md absolute bottom-0 right-0 z-20"
        :class="
          isOrderBuy
            ? 'border border-brand-primary border-opacity-10'
            : 'border border-critical-secondary border-opacity-20 darkTheme:border-opacity-10'
        "
      >
        <NBox
          v-if="PETROCHEMICALS_ISINS.includes(symbol.isin!)"
          class="flex items-center p-3 !bg-neutral-secondary border border-neutral-secondary h-10 cursor-pointer mb-6"
          @click="openPetrochemicalsOrderRiskDescriptionDialog()"
        >
          <div
            class="w-6 h-6 flex items-center justify-center border border-neutral-tertiary ml-1 rounded-lg"
          >
            <NegarErrorBoldIcon size="16" class="text-warning-secondary" />
          </div>

          <NText
            variant="caption1"
            weight="bold"
            class="mr-2 text-neutral-secondary"
          >
            {{ t('orders.symbolUnderCautionTitle') }}
          </NText>
        </NBox>
        <div class="flex gap-2 items-center justify-center">
          <NButton
            v-if="isOrderBuy"
            v-gid="{
              module: 'order',
              screen: 'home',
              section: isEditOrder ? 'editOrderDialog' : 'buyOrderDialog',
              component: isEditOrder ? 'editOrderButton' : 'submitOrderButton',
              disabled: loading || !meta.valid || isOrderStatusPending,
            }"
            variant="fill"
            class="font-bold flex-[2]"
            type="submit"
            size="small"
            :loading="loading"
            :disabled="loading || !meta.valid || isOrderStatusPending"
          >
            {{
              isEditOrder
                ? t('modules.order.editOrder')
                : t('modules.order.sendBuyOrder')
            }}
          </NButton>

          <NButton
            v-else
            v-gid="{
              module: 'order',
              screen: 'home',
              section: isEditOrder ? 'editOrderDialog' : 'sellOrderDialog',
              component: isEditOrder ? 'editOrderButton' : 'submitOrderButton',
              disabled: loading || !meta.valid || isOrderStatusPending,
            }"
            variant="fill"
            class="font-bold flex-[2]"
            type="submit"
            color="red"
            size="small"
            :loading="loading"
            :disabled="loading || !meta.valid || isOrderStatusPending"
          >
            {{
              isEditOrder
                ? t('modules.order.editOrder')
                : t('modules.order.sendSellOrder')
            }}
          </NButton>

          <NButton
            v-if="!isEditOrder"
            v-gid="{
              module: 'order',
              screen: 'home',
              section: `${type}OrderDialog`,
              component: 'sendAndCloseButton',
              disabled: loading || !meta.valid || isOrderStatusPending,
            }"
            variant="outline"
            class="font-bold text-c1"
            :color="isOrderBuy ? 'green' : 'red'"
            size="small"
            :loading="loading"
            :disabled="loading || !meta.valid || isOrderStatusPending"
            @click="
              () => {
                shouldCloseDialogAfterOrderExecution = true;
                handleSubmit();
              }
            "
          >
            {{ t('modules.order.sendAndClose') }}
          </NButton>
        </div>
      </div>
    </form>
  </VeeForm>
</template>

<script setup lang="ts">
import { computed, reactive, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import {
  NegarLinkIcon,
  NegarFlashLightIcon,
  NegarFlashBoldIcon,
  NegarArrowDownIcon,
  NegarErrorBoldIcon,
  NegarArrowLeftIcon,
} from '@negar/icons';
import { useLiveCashQuery } from '@core/cash';
import {
  useSymbolDeductions,
  calcSymbolBuyValue,
  calcSymbolQuantityBuy,
  calcSymbolQuantitySell,
  calcSymbolSellValue,
  PETROCHEMICALS_ISINS,
  getSymbolName,
} from '@core/symbol';
import { omitBy } from '@libs/utils';
import { Instrument, OrderItem, PortfolioItem } from '@libs/api-reference';
import {
  isHeadlineTime,
  useGetServerClock,
  VALIDITY_TYPE_LABEL,
  VALIDITY_TYPES,
} from '@core/order';
import { useDialog, useToast } from '@negar/core';
import { useQueryClient } from '@libs/use';

import {
  FlashTrading,
  useCreateOrder,
  useHandleOrderSocketError,
  useOrderDialogs,
} from '@/modules/order';

import NumberFormatter from '@/components/Number.vue';
import Datepicker from '@/components/Datepicker/index.vue';
import Slider from '@/components/Slider.vue';

import vMask from '@/directives/mask';
import vFocus from '@/directives/focus';

import eventBus from '@/composables/event/eventBus';
import { IPO_QUERY_KEYS, useCreateInitialPublicOffer } from '@core/ipo';
import { usePortfolioDialogs } from '@/modules/portfolio';

type FormModel = {
  quantity?: number | null;
  price?: number | null;
  totalValue?: number | null;
  executedQuantity: number | null;
  validityType: number;
  validityDate: Date;
  remainingQuantity?: number | null;
};

type OrderFormProps = {
  validateOnMount?: boolean;
  type: 'sell' | 'buy';
  symbol: Instrument;
  portfolioQuantity: number;
  model: {
    quantity?: null | number;
    price?: null | number;
  };
  isEditOrder?: boolean;
  isLinkToPrice?: boolean;
  dialogId?: string;
  selectedOrder?: OrderItem;
  isOrderStatusPending?: boolean;
  isUnderWriting?: boolean;
  bonusShare?: PortfolioItem['bonusShare'];
};

const props = withDefaults(defineProps<OrderFormProps>(), {
  isEditOrder: false,
  dialogId: undefined,
  selectedOrder: undefined,
  bonusShare: undefined,
});

const toast = useToast();
const { t, d } = useI18n();
const { selectedDialogId } = useDialog();
const { openBonusShareDialog } = usePortfolioDialogs();

const queryClient = useQueryClient();

const { cashData: cash, isLoadingCash } = useLiveCashQuery();

const { findWageConfigBySymbol } = useSymbolDeductions();
const wage = findWageConfigBySymbol(props.symbol);

const { unsuccessfulOrderId } = useHandleOrderSocketError();
const {
  openOfflineOrderSubmissionDialog,
  openPetrochemicalsOrderRiskDescriptionDialog,
} = useOrderDialogs();

const { time, isErrorLoadingServerClock } = useGetServerClock();

const {
  isLoadingUpdateOrder,
  isLoadingCreateOrder,
  isLoadingCreateBasketOrder,
  createBasketOrder,
  updateOrder,
  createOrder,
  submittedCreateOrderId,
} = useCreateOrder({
  isin: props.symbol.isin as string,
  orderType: props.type,
  selectedOrder: props.selectedOrder,
});

const {
  createInitialPublicOffer,
  isPending: isLoadingCreateInitialPublicOffer,
} = useCreateInitialPublicOffer({
  onSuccess() {
    toast.neutral({
      message: t('ipo.createOrderMessage'),
      duration: 3000,
    });

    queryClient.invalidateQueries(IPO_QUERY_KEYS.ipo);
  },
  onReject(error) {
    const message = error.response?.data[0].message;

    if (message) {
      toast.negative({
        message,
        duration: 3000,
      });
    } else {
      toast.negative({
        message: t('modules.order.error_creating_order_message'),
        duration: 3000,
      });
    }
  },
});

const loading = computed(
  () =>
    isLoadingCreateOrder.value ||
    isLoadingCreateBasketOrder.value ||
    isLoadingUpdateOrder.value ||
    isLoadingCreateInitialPublicOffer.value
);

const isLinkToPrice = ref(props.isLinkToPrice);
const isShowValidityTypeOptions = ref(false);
const isFlashTrading = ref(false);
const hasSuggestions = ref(false);
const shouldCloseDialogAfterOrderExecution = ref(false);
const blockedValue = ref(0);

const BASE_ORDER_FORM: FormModel = {
  quantity: props.model.quantity,
  price: props.model.price,
  remainingQuantity: props.model.quantity,
  totalValue: null,
  executedQuantity: props.symbol.maximumOrderQuantity,
  validityType: props.selectedOrder?.validityTypeId
    ? props.selectedOrder.validityTypeId
    : VALIDITY_TYPES.Day,
  validityDate: props.selectedOrder?.validityDate
    ? new Date(props.selectedOrder.validityDate)
    : new Date(),
};

const formModel = reactive<FormModel>({ ...BASE_ORDER_FORM });

const MAXIMUM_QUANTITY = props.symbol.maximumOrderQuantity
  ? props.symbol.maximumOrderQuantity * 50
  : null;

const isOrderBuy = computed(() => {
  return props.type === 'buy';
});

const realValue = computed(() => {
  if (isOrderBuy.value) {
    return calcSymbolBuyValue(formModel, wage);
  } else {
    return calcSymbolSellValue(formModel, wage);
  }
});

const quantity = computed(() => {
  let roundUp;

  if (formModel.totalValue) {
    roundUp = formModel.totalValue >= cash.value.buyingPower;
  }

  if (isOrderBuy.value) {
    return calcSymbolQuantityBuy(formModel, wage, roundUp);
  } else {
    return calcSymbolQuantitySell(formModel, wage, roundUp);
  }
});

const changeTotalValue = (value) => {
  if (Number(value) >= 0) {
    formModel.totalValue = value;

    if (formModel.price) {
      formModel.quantity = quantity.value;
    }
  }
};

const changePrice = (value) => {
  formModel.price = value;

  if (formModel.quantity) {
    formModel.totalValue = realValue.value;
  } else if (formModel.totalValue) {
    formModel.quantity = quantity.value;
  }
};

const changeQuantity = (value) => {
  formModel.quantity = value;

  if (formModel.price) {
    formModel.totalValue = realValue.value;
  }
};

const onSubmit = (model, { resetForm }) => {
  const resetFormModel = () => {
    Object.assign(formModel, BASE_ORDER_FORM);
    resetForm({
      values: { ...BASE_ORDER_FORM },
    });
  };

  if (props.isUnderWriting) {
    createInitialPublicOffer({
      instrumentIsin: props.symbol.isin,
      price: formModel.price,
      quantity: formModel.quantity,
    });

    return;
  }

  if (props.isEditOrder) {
    updateOrder({
      formModel,
      resetFormModel,
      shouldCloseDialog: true,
    });

    return;
  } else if (!props.isEditOrder && isFlashTrading.value) {
    createBasketOrder({
      formModel,
      resetFormModel,
      shouldCloseDialog: shouldCloseDialogAfterOrderExecution.value,
    });

    return;
  }

  createOrder({
    formModel,
    resetFormModel,
    shouldCloseDialog: shouldCloseDialogAfterOrderExecution.value,
  });
};
const changeLink = () => {
  isLinkToPrice.value = !isLinkToPrice.value;
  isOrderBuy.value
    ? changePrice(headlinePrices.value.minBidPrice)
    : changePrice(headlinePrices.value.maxAskPrice);
};

const validRangePrice = (price) =>
  props.symbol.upperPriceThreshold && props.symbol.lowerPriceThreshold
    ? !(
        price > props.symbol?.upperPriceThreshold ||
        price < props.symbol?.lowerPriceThreshold
      )
    : true;

// Calculate maximum buy price and minimum sell price in bidAsk table for link to headline
const headlinePrices = computed(() => {
  const validRangeForAskPrice: number[] = [];
  const validRangeForBidPrice: number[] = [];

  props.symbol?.bidAsk?.map((o) => {
    if (validRangePrice(o.askPrice)) {
      validRangeForAskPrice.push(o.askPrice);
    }
  });

  props.symbol?.bidAsk?.map((o) => {
    if (validRangePrice(o.bidPrice)) {
      validRangeForBidPrice.push(o.bidPrice);
    }
  });

  const maxAskPrice = Math.max(...validRangeForAskPrice);
  const minBidPrice = Math.min(...validRangeForBidPrice);

  return {
    maxAskPrice: validRangeForAskPrice.length
      ? maxAskPrice
      : props.symbol.lowerPriceThreshold,
    minBidPrice: validRangeForBidPrice.length
      ? minBidPrice
      : props.symbol.upperPriceThreshold,
  };
});

watch(
  headlinePrices,
  (value) => {
    if (isLinkToPrice.value) {
      isOrderBuy.value
        ? changePrice(value.minBidPrice)
        : changePrice(value.maxAskPrice);
    }
  },
  {
    immediate: true,
  }
);

const validityTypeButtonClasses = {
  buy: 'bg-brand-secondary text-brand-primary',
  sell: 'bg-critical-secondary text-critical-primary',
};

const validityTypeLabel = computed(() =>
  formModel.validityType === VALIDITY_TYPES.GoodTillDate
    ? `${t('orders.valid_until')} ${d(formModel.validityDate, 'shortDate')}`
    : VALIDITY_TYPE_LABEL[formModel.validityType]
);

eventBus.on('executeOrder', (shouldClose) => {
  if (selectedDialogId.value === props.dialogId && !loading.value) {
    shouldCloseDialogAfterOrderExecution.value = shouldClose;

    onSubmit(formModel, {
      resetForm: () => {
        Object.assign(formModel, BASE_ORDER_FORM);
      },
    });
  }
});

watch(
  () => props.model,
  (newModel) => {
    const sanitizedModel = omitBy(
      newModel,
      (v) => v === null || v === undefined
    );

    if (Object.keys(sanitizedModel).length) {
      Object.assign(formModel, sanitizedModel);
    }

    if (formModel.price) {
      changePrice(formModel.price);
      blockedValue.value = formModel.totalValue ?? 0;
    }

    if (formModel.quantity) {
      changeQuantity(formModel.quantity);
      blockedValue.value = formModel.totalValue ?? 0;
    }
  },
  {
    deep: true,
    immediate: true,
  }
);

const maximumBuyingPower = computed(() =>
  props.isEditOrder
    ? cash.value.buyingPower + blockedValue.value
    : cash.value.buyingPower
);

watch(
  () => formModel.quantity,
  (newQuantity) => {
    if (
      newQuantity &&
      !props.isUnderWriting &&
      props.symbol.maximumOrderQuantity &&
      newQuantity > props.symbol.maximumOrderQuantity
    ) {
      isFlashTrading.value = true;

      return;
    }

    isFlashTrading.value = false;
  },
  { immediate: true }
);

watch(unsuccessfulOrderId, () => {
  if (
    !isErrorLoadingServerClock.value &&
    !isHeadlineTime(time.value) &&
    submittedCreateOrderId.value &&
    unsuccessfulOrderId.value === submittedCreateOrderId.value
  ) {
    openOfflineOrderSubmissionDialog({
      isin: props.symbol.isin as string,
      type: props.type,
      formModel: { ...formModel },
    });
  }
});
</script>
